import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useOptimizedFamily } from '@/hooks/useOptimizedFamily';
import { useToast } from '@/hooks/use-toast';
import { useClanSwitch } from '@/contexts/ClanSwitchContext';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { startNewFamilyOnboarding } from '@/utils/onboardingUtils';


export function FamilySelector() {
  const { families, currentFamily, createFamily, loading } = useOptimizedFamily();
  const { toast, dismiss } = useToast();
  const { showClanLoader, hideClanLoader } = useClanSwitch();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateNewFamily = async () => {
    if (isCreating) return;
    
    setIsCreating(true);

    try {
      // Clear any existing onboarding data and set new family mode
      startNewFamilyOnboarding();
      navigate('/new-user-onboarding');
      
    } catch (error) {
      console.error('Erreur lors de la création de la famille:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer la famille. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleValueChange = async (value: string) => {
    if (value === 'CREATE_NEW') {
      handleCreateNewFamily();
    } else {
      console.log(`🔄 Family selector switching to: ${value}`);
      
      // Find the selected family name
      const selectedFamily = families.find(f => f.family.id === value);
      const familyName = selectedFamily?.family.name || 'inconnu';
      
      try {
        // 1. Dismiss all notifications
        dismiss();

        // 2. Show loading overlay
        showClanLoader(familyName);

        // 3. Set new family ID in localStorage
        localStorage.setItem('currentFamilyId', value);

        // 4. Clear caches and realtime channels
        await queryClient.cancelQueries({ predicate: () => true });
        queryClient.clear();
        supabase.removeAllChannels();

        // 5. Brief delay to ensure cache clearing completes
        await new Promise(resolve => setTimeout(resolve, 100));

        // 6. Navigate to refresh components and refetch data
        const currentPath = location.pathname;
        navigate('/loading', { replace: true });
        
        // 7. Navigate back to trigger fresh data load
        setTimeout(() => {
          navigate(currentPath, { replace: true });
          
          // 8. Refetch all queries and hide loader
          setTimeout(() => {
            queryClient.refetchQueries();
            hideClanLoader();
          }, 200);
        }, 100);
      } catch (error) {
        console.error('Erreur lors du changement de famille:', error);
        hideClanLoader();
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="bg-transparent text-white h-8 w-32 flex items-center">
          Chargement...
        </div>
      </div>
    );
  }

  // Hide family selector when there are no families
  if (!families || families.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center space-x-2 relative z-[80]">
      {/* Family Selector */}
      <Select
        value={currentFamily?.family.id || ''}
        onValueChange={handleValueChange}
      >
        <SelectTrigger className="w-auto min-w-fit relative z-[80] bg-transparent border-transparent text-white [&>span:first-child]:flex-1 [&>span:first-child]:text-left gap-1 px-0 focus:ring-0 focus:ring-offset-0 focus:outline-none focus:border-transparent hover:border-transparent active:border-transparent data-[state=open]:border-transparent data-[state=open]:ring-0 data-[state=open]:ring-offset-0 shadow-none focus:shadow-none hover:shadow-none active:shadow-none data-[state=open]:shadow-none">
          <SelectValue placeholder="Sélectionner clan">
            {currentFamily && (
              <div className="flex items-center">
                <span className="truncate">Clan {currentFamily.family.name}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="z-[90] bg-white/10 text-white backdrop-blur-lg border-white/20 shadow-lg">
          {families.map((familyWithMembership) => (
            <SelectItem 
              key={familyWithMembership.family.id} 
              value={familyWithMembership.family.id}
              className="pl-3 pr-3 py-2 hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-transparent [&>span:first-child]:hidden"
            >
              <div className="flex items-center justify-between w-full">
                <span>Clan {familyWithMembership.family.name}</span>
                {currentFamily?.family.id === familyWithMembership.family.id && (
                  <span className="ml-2 h-2 w-2 rounded-full bg-blue-500"></span>
                )}
              </div>
            </SelectItem>
          ))}
          <SelectItem 
            value="CREATE_NEW" 
            className={`pl-3 pr-3 py-2 hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-transparent [&>span:first-child]:hidden ${isCreating ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center">
              <span className="mr-2">+</span>
              <span>{isCreating ? 'Création en cours...' : 'Créer nouveau clan'}</span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}